import { Component, OnDestroy, ViewEncapsulation } from '@angular/core';
import { NavStateService, INavStateConfig, SideNavMode, SideNavState } from '../services/nav-state/nav-state.service';
import { NavItemsService } from '../services/nav-items/nav-items.service';
import { combineLatest, Observable } from 'rxjs';
import { CommonModule } from '@angular/common';
import { UrlNavigationService } from '../../services/url-navigation/url-navigation.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CdkOverlayOrigin, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { NvLogoModule } from 'projects/shared/nvps-libraries/design/nv-logo/nv-logo.module';
import { NvIconButtonModule } from 'projects/shared/nvps-libraries/design/nv-icon-button/nv-icon-button.module';
import { NvSidebarListModule } from 'Src/nvps-libraries/design/nv-sidebar-list/nv-sidebar-list.module';
import { TDistrictId } from 'Src/ng2/shared/typings/interfaces/district.interface';
import { TPortalLocation } from 'Src/ng2/shared/typings/interfaces/portal.interface';
import { distinctUntilChanged, filter, map as rxMap, startWith, tap } from 'rxjs/operators';
import { NvSquareButtonModule } from 'Src/nvps-libraries/design/nv-square-button/nv-square-button.module';
import { SessionStorageService } from '../../services/web-storage/session-storage/session-storage.service';
import { TOpEntityType } from '../../typings/types/entity-type.types';
import { IFeatureSetNavConfigResultDecorated, IFeatureSetNavItem, IFeatureSetNavItemDecorated } from '../../typings/interfaces/feature-set-nav-config.interface';
import { UrlPathService } from '../../services/url-path-service/url-path.service';
import { TEntityMode } from '../../typings/types/entity-mode.types';
import { WindowRef } from 'projects/shared/services/window-ref/windowRef';
import { SharedEventTrackers } from '../../services/mixpanel/event-trackers/shared-tracking.service';
import { getPortalLocationFromEntityType } from '../../helpers/portal/portal.helper';
import { getPageClassificationFromRoute } from '../../helpers/page-classification/page-classification.helper';

interface ISquareNavDisplayItem {
  activeItem: IFeatureSetNavItemDecorated; // could be itself or first child if it has children
  icon: string;
  isSelected: boolean;
  item: IFeatureSetNavItemDecorated;
}

interface IGlobalSideNavViewModel {
  navSections: IFeatureSetNavItemDecorated[][];
  squareNavSections: ISquareNavDisplayItem[][];
  showCollapsed: boolean;
  showExpandedOrOverOpened: boolean;
  showControls: boolean;
  sideNav: {
    sideNavState: INavStateConfig['sideNav']['sideNavState'];
    matSideNavMode: INavStateConfig['sideNav']['matSideNavMode'];
  };
}

@Component({
  selector: 'global-side-nav-content',
  templateUrl: './global-side-nav-content.component.html',
  styleUrls: ['./global-side-nav-content.component.scss'],
  imports: [CommonModule, OverlayModule, NvLogoModule, NvIconButtonModule, NvSidebarListModule, NvSquareButtonModule],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  providers: [UrlNavigationService],
})
export class GlobalSideNavContentComponent implements OnDestroy {
  public navStateConfig$: Observable<INavStateConfig>;
  public navItems$: Observable<IFeatureSetNavConfigResultDecorated>;

  public vm$: Observable<IGlobalSideNavViewModel>;
  public selectedSidebarKey: string;
  private entityMode: TEntityMode;
  private portal: TPortalLocation;
  // Rebuilt with the view model, so it always reflects the nav the user is actually looking at.
  private navGroupKeys = new Set<string>();
  private districtId: TDistrictId;

  private entityType: TOpEntityType;
  private entityId: string;
  private networkId: string;
  private subEntityId: string;
  private subEntityParamName: string;

  // overlay
  public readonly overlayPanelClassName = 'global-side-nav-collapsed-children-overlay-pane';
  private readonly overlayCloseDelayMs = 50;
  public activeOverlayParentKey: string | null = null;
  public activeOverlayParentItem: IFeatureSetNavItem | null = null;
  public activeOverlayOrigin: CdkOverlayOrigin | null = null;
  public canShowHoverOverlay: boolean = false;
  private closeOverlayTimeoutId: number | null = null;
  public readonly overlayPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'bottom',
    },
  ];

  constructor (
    private navStateService: NavStateService,
    private navItemsService: NavItemsService,
    private urlNavigationService: UrlNavigationService,
    private route: ActivatedRoute,
    private router: Router,
    private sessionStorageService: SessionStorageService,
    private urlPathService: UrlPathService,
    private windowRef: WindowRef,
    private sharedEventTrackers: SharedEventTrackers,
  ) {}

  ngOnInit (): void {
    this.resolveContext();
    this.resolveHoverOverlayCapability();
    this.vm$ = this.initializeViewModel();
  }

  ngOnDestroy (): void {
    this.clearOverlayCloseTimeout();
  }

  private resolveHoverOverlayCapability (): void {
    this.canShowHoverOverlay = this.windowRef.nativeWindow.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  private clearOverlayCloseTimeout (): void {
    if (this.closeOverlayTimeoutId !== null) {
      this.windowRef.nativeWindow.clearTimeout(this.closeOverlayTimeoutId);
      this.closeOverlayTimeoutId = null;
    }
  }

  private scheduleOverlayClose (): void {
    this.clearOverlayCloseTimeout();
    this.closeOverlayTimeoutId = this.windowRef.nativeWindow.setTimeout(() => {
      this.hideOverlay();
    }, this.overlayCloseDelayMs);
  }

  private isInsideOverlayPane (target: EventTarget | null): boolean {
    const element = target as Element | null;
    return !!element?.closest?.(`.${this.overlayPanelClassName}`);
  }

  private showOverlay (item: IFeatureSetNavItem, origin: CdkOverlayOrigin): void {
    if (!this.canShowHoverOverlay) {
      return;
    }
    if (!item?.children?.length) {
      this.hideOverlay();
      return;
    }
    this.activeOverlayParentKey = item.key;
    this.activeOverlayParentItem = item;
    this.activeOverlayOrigin = origin;
  }

  public hideOverlay (): void {
    this.activeOverlayParentKey = null;
    this.activeOverlayParentItem = null;
    this.activeOverlayOrigin = null;
  }

  // Keeps the parent square button visually highlighted (hover state) while its flyout is open.
  public isOverlayActiveForItem (item: IFeatureSetNavItem): boolean {
    return !!this.activeOverlayParentKey && this.activeOverlayParentKey === item.key;
  }

  public onCollapsedParentMouseEnter (item: IFeatureSetNavItem, origin: CdkOverlayOrigin): void {
    this.clearOverlayCloseTimeout();
    this.showOverlay(item, origin);
  }

  public onCollapsedParentMouseLeave (): void {
    this.scheduleOverlayClose();
  }

  public onOverlayMouseEnter (): void {
    this.clearOverlayCloseTimeout();
  }

  public onOverlayMouseLeave (): void {
    this.scheduleOverlayClose();
  }

  // Defer the close so the clicked child's router navigation completes before the
  // overlay (and its <a routerLink>) is torn down; a synchronous close removes the
  // anchor mid-click and triggers a full page reload.
  public onOverlayChildSelect (selectedNavItemKey?: string): void {
    this.trackNavItemClick(selectedNavItemKey);
    this.scheduleOverlayClose();
  }

  public onCollapsedParentFocusIn (item: IFeatureSetNavItem, origin: CdkOverlayOrigin): void {
    this.showOverlay(item, origin);
  }

  public onCollapsedParentFocusOut (event: FocusEvent): void {
    const currentTarget = event.currentTarget as HTMLElement | null;
    const relatedTarget = event.relatedTarget as Node | null;
    if (this.isInsideOverlayPane(relatedTarget)) {
      return;
    }
    if (currentTarget && relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }
    this.hideOverlay();
  }

  private resolveContext (): void {
    const { entityType, entityMode, entityId, networkId, subEntityId } = UrlNavigationService.getOpContext(this.route.snapshot);
    this.entityMode = entityMode;
    this.entityId = entityId;
    this.entityType = entityType as TOpEntityType;
    this.portal = getPortalLocationFromEntityType(this.entityType, this.entityMode);
    this.networkId = networkId;
    this.districtId = this.sessionStorageService.getItem('currentDistrict') as TDistrictId;
    this.subEntityId = subEntityId;
    this.subEntityParamName = UrlNavigationService.getSubEntityParamName(this.entityType);
  }

  private initializeViewModel (): Observable<IGlobalSideNavViewModel> {
    this.navStateConfig$ = this.navStateService.getLatestConfig$();
    this.navItems$ = this.navItemsService.getNavItems$({
      context: {
        entityMode: this.entityMode,
        entityId: this.entityId,
        entityType: this.entityType,
        networkId: this.networkId,
        districtId: this.districtId,
        subEntityId: this.subEntityId,
        subEntityParamName: this.subEntityParamName,
      },
    });

    const activePath$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null),
      rxMap(() => this.router.routerState.snapshot.url),
      distinctUntilChanged(),
      rxMap(url => this.urlPathService.getFeaturePath(url)),
    );

    const navItemsWithSelection$ = combineLatest([this.navItems$, activePath$]).pipe(
      tap(([{ base, optional }, activePath]) => {
        this.selectByActivePath([...base, ...optional], activePath);
      }),
      rxMap(([navItems]) => navItems),
    );

    const vm$ = combineLatest([this.navStateConfig$, navItemsWithSelection$]).pipe(
      rxMap(([navStateConfig, navItems]) => this.buildViewModel(navStateConfig, navItems)),
    );
    return vm$;
  }

  private buildSquareNavSections (sections: IFeatureSetNavItemDecorated[][]): ISquareNavDisplayItem[][] {
    return sections.map((section: IFeatureSetNavItemDecorated[]) => section.map((item: IFeatureSetNavItemDecorated) => this.toSquareNavDisplayItem(item)));
  }

  private toSquareNavDisplayItem (item: IFeatureSetNavItemDecorated): ISquareNavDisplayItem {
    const isSelected = this.isSquareNavItemSelected(item);
    return {
      activeItem: this.resolveActiveSquareNavItem(item),
      icon: this.getSquareNavItemIcon(item, isSelected),
      isSelected,
      item,
    };
  }

  private isSquareNavItemSelected (item: IFeatureSetNavItemDecorated): boolean {
    if (item.children.length) {
      return item.children.some((child: IFeatureSetNavItem) => {
        return child.key === this.selectedSidebarKey;
      });
    }

    return item.key === this.selectedSidebarKey;
  }

  private getSquareNavItemIcon (item: IFeatureSetNavItemDecorated, isSelected: boolean): string {
    return (isSelected ? item.leftSelectedIcon : item.leftDefaultIcon);
  }

  // business rule is to default to the first child with a valid url
  private resolveActiveSquareNavItem (item: IFeatureSetNavItemDecorated): IFeatureSetNavItemDecorated {
    const children = item.children;
    if (!children?.length) {
      return item;
    }

    // Return first child with a url, or fall back to first child
    const firstChildWithUrl = children.find((child) => child.url);
    return firstChildWithUrl || children[0];
  }

  private selectByActivePath (featureSetNavItems: IFeatureSetNavItem[], activePath: string): void {
    const match = this.navItemsService.findBestActivePathMatch(featureSetNavItems, activePath);
    this.selectedSidebarKey = match?.key || null;
  }

  private buildViewModel (
    navStateConfig: INavStateConfig,
    navItems: IFeatureSetNavConfigResultDecorated,
  ): IGlobalSideNavViewModel {
    const {
      sideNav: { sideNavState, matSideNavMode },
    } = navStateConfig;
    const { base, optional } = navItems;
    const navSections = [base, optional];
    this.navGroupKeys = this.collectNavGroupKeys(navSections.flat());

    return {
      navSections,
      squareNavSections: this.buildSquareNavSections(navSections),
      ...this.resolveNavVisibility(sideNavState, matSideNavMode),
      sideNav: { sideNavState, matSideNavMode },
    };
  }

  /**
   * Determines visibility states for nav controls, collapsed, and expanded/over-opened views.
   * @param sideNavState The current state of the side nav
   * @param matSideNavMode The current mode of the side nav
   */
  private resolveNavVisibility (
    sideNavState: INavStateConfig['sideNav']['sideNavState'],
    matSideNavMode: INavStateConfig['sideNav']['matSideNavMode'],
  ): Pick<IGlobalSideNavViewModel, 'showControls' | 'showCollapsed' | 'showExpandedOrOverOpened'> {
    return {
      showControls: this.shouldShowControls(sideNavState, matSideNavMode),
      showCollapsed: this.shouldShowCollapsed(sideNavState, matSideNavMode),
      showExpandedOrOverOpened: this.shouldShowExpandedOrOverOpened(sideNavState, matSideNavMode),
    };
  }

  private shouldShowControls (
    sideNavState: INavStateConfig['sideNav']['sideNavState'],
    matSideNavMode: INavStateConfig['sideNav']['matSideNavMode'],
  ): boolean {
    return matSideNavMode === SideNavMode.OVER && sideNavState === SideNavState.OVER_OPENED;
  }

  private shouldShowCollapsed (
    sideNavState: INavStateConfig['sideNav']['sideNavState'],
    matSideNavMode: INavStateConfig['sideNav']['matSideNavMode'],
  ): boolean {
    return matSideNavMode === SideNavMode.SIDE && sideNavState === SideNavState.SIDE_COLLAPSED;
  }

  private shouldShowExpandedOrOverOpened (
    sideNavState: INavStateConfig['sideNav']['sideNavState'],
    matSideNavMode: INavStateConfig['sideNav']['matSideNavMode'],
  ): boolean {
    return (
      (matSideNavMode === SideNavMode.SIDE && sideNavState === SideNavState.SIDE_EXPANDED) ||
      (matSideNavMode === SideNavMode.OVER && sideNavState === SideNavState.OVER_OPENED)
    );
  }

  // ── Event handlers ──────────────────────────────────────────────────────────

  /**
   * Clicking a nav group only expands or collapses its accordion — it navigates nowhere, since
   * `decorateItem` leaves a parent's `url` null. `nv-sidebar-list` emits `sidebarListItemSelect` for
   * a group header exactly as it does for a leaf, so without this guard a toggle is indistinguishable
   * from a navigation in the report.
   *
   * The classification describes where the user navigated *from*. The destination is already carried by
   * `navItem`, so classifying it too would give reports two sources for one fact.
   */
  public trackNavItemClick (navItemKey: string): void {
    if (!navItemKey || this.navGroupKeys.has(navItemKey)) {
      return;
    }
    const { featureSet, pageTemplate } = getPageClassificationFromRoute(this.router.routerState.snapshot.root);

    this.sharedEventTrackers.trackLefthandNavEvent({
      navItem: navItemKey,
      portal: this.portal,
      featureSet,
      pageTemplate,
    });
  }

  /** Keys of every item with children, at any depth — those render as expandable accordion nodes. */
  private collectNavGroupKeys (items: IFeatureSetNavItemDecorated[]): Set<string> {
    return items.reduce((keys, item) => {
      if (!item?.children?.length) return keys;

      keys.add(item.key);
      this.collectNavGroupKeys(item.children as IFeatureSetNavItemDecorated[])
        .forEach(childKey => keys.add(childKey));
      return keys;
    }, new Set<string>());
  }

  public onLogoClick (): void {
    this.urlNavigationService.navigateToPortal({ entityId: this.entityId, entityType: this.entityType, networkId: this.networkId });
  }

  public onBurgerClick (): void {
    this.navStateService.toggleSideNav();
  }
}
